import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
    Save, Edit2, X, Plus, Trash2, FlaskConical,
    Building2, Phone, Mail, MapPin, CreditCard, Percent,
    CheckCircle2, XCircle, RefreshCw, ChevronRight,
} from 'lucide-react';
import { toast } from 'react-toastify';
import LabServices from '../../shared/ApiServices/LabServices';
import useLabPermissions from './useLabPermissions';
import {
    extractDoctorsList, extractDoctorId, extractDoctorName,
    type ReferenceDoctor,
} from './labUtils';

// ─── Full config shape from API ───────────────────────────────────────────────
interface LabProfileData {
    labName?: string; lab_name?: string;
    contact?: string; address?: string; email?: string;
    configType?: string; configId?: string;
    PK?: string; SK?: string; updatedAt?: string;
}
interface SampleTypeData {
    typeId?: string; SK?: string;
    sampleTypeName?: string; sample_type_name?: string;
    description?: string; isActive?: string | boolean;
    bottleColor?: string; department?: string;
}
interface PaymentModeData {
    modeId?: string; SK?: string;
    modeName?: string; mode_name?: string;
    isActive?: string | boolean;
}
interface TaxConfigData {
    taxId?: string; SK?: string;
    taxName?: string; tax_name?: string;
    rate?: number; isActive?: string | boolean;
}
interface FullConfig {
    lab_profile: LabProfileData[];
    sample_type: SampleTypeData[];
    payment_mode: PaymentModeData[];
    tax_config: TaxConfigData[];
}

// ─── Shared UI primitives ─────────────────────────────────────────────────────
const inputCls = 'w-full rounded-xl border border-[var(--v2-border-interactive)] bg-[var(--v2-bg-surface)] px-3 py-2 text-[13px] text-[var(--v2-text-primary)] outline-none transition-colors';

const Field: React.FC<{ label: string; required?: boolean; children: React.ReactNode }> = ({ label, required, children }) => (
    <div className="flex flex-col gap-1.5">
        <label className="text-[10px] font-extrabold uppercase tracking-widest text-[var(--v2-text-muted)]">
            {label}{required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
        {children}
    </div>
);

const SectionCard: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
    <div className={`rounded-2xl border border-[var(--v2-border-card)] bg-[var(--v2-bg-card)] ${className}`}
        style={{ boxShadow: 'var(--v2-shadow-card)' }}>
        {children}
    </div>
);

const ActiveBadge: React.FC<{ active: boolean }> = ({ active }) => (
    <span className="inline-flex items-center gap-1 text-[9px] font-black px-2 py-0.5 rounded-full"
        style={active
            ? { background: 'rgba(5,150,105,0.1)', color: '#059669' }
            : { background: 'rgba(100,100,100,0.08)', color: 'var(--v2-text-muted)' }}>
        {active
            ? <CheckCircle2 className="w-2.5 h-2.5" strokeWidth={2.5} />
            : <XCircle className="w-2.5 h-2.5" strokeWidth={2.5} />}
        {active ? 'Active' : 'Inactive'}
    </span>
);

const isActive = (val?: string | boolean) => val === true || val === 'true' || val === 'True';

// ─── Modal ────────────────────────────────────────────────────────────────────
const Modal: React.FC<{ title: string; onClose: () => void; children: React.ReactNode; width?: string }> =
    ({ title, onClose, children, width = 'max-w-lg' }) => createPortal(
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4"
            style={{ background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(6px)' }}>
            <div className={`w-full ${width} rounded-2xl border border-[var(--v2-border-card)] bg-[var(--v2-bg-card)] shadow-2xl flex flex-col max-h-[90vh]`}>
                <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--v2-border-subtle)]">
                    <h2 className="text-[14px] font-bold text-[var(--v2-text-heading)] m-0">{title}</h2>
                    <button type="button" onClick={onClose}
                        className="w-8 h-8 rounded-lg flex items-center justify-center cursor-pointer text-[var(--v2-text-muted)] hover:text-[var(--v2-text-primary)] hover:bg-[var(--v2-bg-surface)] transition-colors border-0 bg-transparent">
                        <X className="w-4 h-4" />
                    </button>
                </div>
                <div className="overflow-y-auto flex-1 px-5 py-4">{children}</div>
            </div>
        </div>,
        document.body
    );

// ═══════════════════════════════════════════════════════════════════════════════
// SECTION 1 — LAB PROFILE
// ═══════════════════════════════════════════════════════════════════════════════
const LabProfileSection: React.FC<{ profile: LabProfileData | null; loading: boolean; canManage: boolean; onSaved: () => void }> =
    ({ profile, loading, canManage, onSaved }) => {
        const [editing, setEditing] = useState(false);
        const [saving, setSaving] = useState(false);
        const [form, setForm] = useState({ labName: '', contact: '', email: '', address: '' });

        useEffect(() => {
            if (profile) {
                setForm({
                    labName: profile.labName ?? profile.lab_name ?? '',
                    contact: profile.contact ?? '',
                    email: profile.email ?? '',
                    address: profile.address ?? '',
                });
            }
        }, [profile]);

        const handleSave = async () => {
            if (!form.labName.trim()) { toast.error('Lab name is required'); return; }
            setSaving(true);
            try {
                // PUT /lab/config returns: { message: "Lab config updated" }
                const res: any = await LabServices.updateLabConfig({
                    configType: 'LAB_PROFILE',
                    configId: profile?.configId ?? 'SETTINGS',
                    ...form,
                });
                toast.success(res?.message ?? 'Lab profile updated');
                setEditing(false);
                onSaved();
            } catch (err: any) { toast.error(err?.message ?? 'Save failed'); }
            finally { setSaving(false); }
        };

        if (loading) {
            return (
                <SectionCard className="p-6">
                    <div className="flex flex-col gap-4">
                        {[1, 2, 3, 4].map(i => (
                            <div key={i} className="flex flex-col gap-2">
                                <div className="h-3 w-16 rounded" style={{ background: 'var(--v2-border-subtle)' }} />
                                <div className="h-10 rounded-xl" style={{ background: 'var(--v2-border-subtle)' }} />
                            </div>
                        ))}
                    </div>
                </SectionCard>
            );
        }

        const FIELDS = [
            { key: 'labName', label: 'Lab Name', icon: Building2, placeholder: 'Symbian Diagnostics Lab', required: true },
            { key: 'contact', label: 'Contact Number', icon: Phone, placeholder: '+1 555 0198', required: false },
            { key: 'email', label: 'Email Address', icon: Mail, placeholder: 'lab@hospital.com', required: false },
            { key: 'address', label: 'Address', icon: MapPin, placeholder: '123 Health Ave, Metropolis', required: false },
        ] as const;

        return (
            <SectionCard>
                {/* Card header */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--v2-border-subtle)]">
                    <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
                            style={{ background: 'linear-gradient(135deg,#059669,#047857)', boxShadow: '0 2px 8px rgba(5,150,105,.3)' }}>
                            <Building2 className="w-4 h-4 text-white" strokeWidth={2} />
                        </div>
                        <div>
                            <p className="text-[13px] font-bold text-[var(--v2-text-heading)] m-0">
                                {form.labName || 'Lab Profile'}
                            </p>
                            {profile?.updatedAt && (
                                <p className="text-[9px] text-[var(--v2-text-muted)] m-0">
                                    Updated {new Date(profile.updatedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                                </p>
                            )}
                        </div>
                    </div>
                    {canManage && !editing && (
                        <button type="button" onClick={() => setEditing(true)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[11px] font-bold cursor-pointer border border-[var(--v2-border-card)] text-[var(--v2-text-muted)] hover:text-[var(--v2-text-primary)] bg-transparent transition-all">
                            <Edit2 className="w-3.5 h-3.5" strokeWidth={2} /> Edit
                        </button>
                    )}
                </div>

                {/* Fields */}
                <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {FIELDS.map(({ key, label, icon: Icon, placeholder, required }) => (
                        <Field key={key} label={label} required={required && editing}>
                            {editing ? (
                                <div className="relative">
                                    <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[var(--v2-text-muted)]" strokeWidth={1.5} />
                                    <input
                                        className={`${inputCls} pl-9`}
                                        placeholder={placeholder}
                                        value={(form as any)[key]}
                                        onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                                    />
                                </div>
                            ) : (
                                <div className="flex items-center gap-2 py-2">
                                    <Icon className="w-3.5 h-3.5 text-[var(--v2-text-muted)] flex-shrink-0" strokeWidth={1.5} />
                                    <span className="text-[13px] text-[var(--v2-text-primary)]">
                                        {(form as any)[key] || <span className="text-[var(--v2-text-muted)] italic">Not set</span>}
                                    </span>
                                </div>
                            )}
                        </Field>
                    ))}
                </div>

                {/* Footer actions */}
                {editing && (
                    <div className="flex gap-2 justify-end px-5 pb-5 pt-1">
                        <button type="button" onClick={() => setEditing(false)}
                            className="px-4 py-2 rounded-xl text-[12px] font-bold cursor-pointer border border-[var(--v2-border-card)] text-[var(--v2-text-muted)] bg-[var(--v2-bg-surface)] hover:bg-[var(--v2-bg-card)] transition-colors">
                            Cancel
                        </button>
                        <button type="button" onClick={handleSave} disabled={saving}
                            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-[12px] font-bold cursor-pointer text-white disabled:opacity-50 border-0 transition-opacity"
                            style={{ background: '#059669' }}>
                            <Save className="w-3.5 h-3.5" strokeWidth={2} />
                            {saving ? 'Saving…' : 'Save Changes'}
                        </button>
                    </div>
                )}
            </SectionCard>
        );
    };

// ═══════════════════════════════════════════════════════════════════════════════
// SECTION 2 — SAMPLE TYPES
// ═══════════════════════════════════════════════════════════════════════════════
const BOTTLE_COLOR_PRESETS = ['Lavender', 'Red', 'Yellow', 'Green', 'Blue', 'Grey', 'Orange', 'Gold'];

const SampleTypesSection: React.FC<{ items: SampleTypeData[]; loading: boolean; canManage: boolean; onSaved: () => void }> =
    ({ items, loading, canManage, onSaved }) => {
        const [showCreate, setShowCreate] = useState(false);
        const [saving, setSaving] = useState(false);
        const [form, setForm] = useState({ sampleTypeName: '', handlingInstructions: '', bottleColor: '', department: '' });

        const handleCreate = async () => {
            if (!form.sampleTypeName.trim()) { toast.error('Sample type name is required'); return; }
            setSaving(true);
            try {
                // POST /lab/sample-types returns: { message, typeId }
                const res: any = await LabServices.createSampleType({
                    sampleTypeName: form.sampleTypeName.trim(),
                    handlingInstructions: form.handlingInstructions,
                    bottleColor: form.bottleColor,
                    department: form.department,
                });
                toast.success(res?.message ?? 'Sample type created');
                setShowCreate(false);
                setForm({ sampleTypeName: '', handlingInstructions: '', bottleColor: '', department: '' });
                onSaved();
            } catch (err: any) { toast.error(err?.message ?? 'Save failed'); }
            finally { setSaving(false); }
        };

        const bottleColorDot = (color?: string) => {
            const map: Record<string, string> = {
                lavender: '#967BB6', red: '#DC2626', yellow: '#D97706', green: '#059669',
                blue: '#2563EB', grey: '#6B7280', orange: '#EA580C', gold: '#B45309',
            };
            return map[(color ?? '').toLowerCase()] ?? '#9CA3AF';
        };

        return (
            <SectionCard>
                <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--v2-border-subtle)]">
                    <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
                            style={{ background: 'linear-gradient(135deg,#7C3AED,#6D28D9)', boxShadow: '0 2px 8px rgba(124,58,237,.3)' }}>
                            <FlaskConical className="w-4 h-4 text-white" strokeWidth={2} />
                        </div>
                        <div>
                            <p className="text-[13px] font-bold text-[var(--v2-text-heading)] m-0">Sample Types</p>
                            <p className="text-[9px] text-[var(--v2-text-muted)] m-0">{items.length} configured</p>
                        </div>
                    </div>
                    {canManage && (
                        <button type="button" onClick={() => setShowCreate(true)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[11px] font-bold cursor-pointer text-white border-0"
                            style={{ background: '#7C3AED' }}>
                            <Plus className="w-3.5 h-3.5" strokeWidth={2.5} /> Add Type
                        </button>
                    )}
                </div>

                {loading ? (
                    <div className="p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {[1, 2, 3].map(i => <div key={i} className="h-20 rounded-xl" style={{ background: 'var(--v2-border-subtle)' }} />)}
                    </div>
                ) : items.length === 0 ? (
                    <div className="flex flex-col items-center py-12 gap-2 text-center">
                        <FlaskConical className="w-8 h-8 text-[var(--v2-text-muted)]" strokeWidth={1.5} />
                        <p className="text-[12px] font-semibold text-[var(--v2-text-muted)]">No sample types configured</p>
                        {canManage && (
                            <button type="button" onClick={() => setShowCreate(true)}
                                className="px-3 py-1.5 rounded-xl text-[11px] font-bold cursor-pointer text-white border-0 mt-1"
                                style={{ background: '#7C3AED' }}>
                                Add first sample type
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {items.map((st, i) => {
                            const name = st.sampleTypeName ?? st.sample_type_name ?? '—';
                            const color = bottleColorDot(st.bottleColor);
                            const active = isActive(st.isActive);
                            return (
                                <div key={st.typeId ?? st.SK ?? i}
                                    className="rounded-xl border border-[var(--v2-border-card)] bg-[var(--v2-bg-surface)] p-3.5 flex flex-col gap-2.5 hover:border-purple-300 transition-colors">
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="flex items-center gap-2 min-w-0">
                                            <div className="w-4 h-4 rounded-full flex-shrink-0 ring-2 ring-white ring-offset-1"
                                                style={{ background: st.bottleColor ? color : 'var(--v2-border-subtle)' }} />
                                            <p className="text-[12px] font-bold text-[var(--v2-text-heading)] m-0 truncate">{name}</p>
                                        </div>
                                        <ActiveBadge active={active} />
                                    </div>
                                    {st.description && (
                                        <p className="text-[10px] text-[var(--v2-text-muted)] m-0 line-clamp-2">{st.description}</p>
                                    )}
                                    <div className="flex items-center gap-2 flex-wrap">
                                        {st.bottleColor && (
                                            <span className="text-[9px] font-bold px-2 py-0.5 rounded-full"
                                                style={{ background: `${color}22`, color }}>
                                                {st.bottleColor}
                                            </span>
                                        )}
                                        {st.department && (
                                            <span className="text-[9px] font-bold px-2 py-0.5 rounded-full"
                                                style={{ background: 'var(--v2-bg-card)', color: 'var(--v2-text-muted)', border: '1px solid var(--v2-border-card)' }}>
                                                {st.department}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {showCreate && (
                    <Modal title="Add Sample Type" onClose={() => setShowCreate(false)}>
                        <div className="flex flex-col gap-3">
                            <Field label="Sample Type Name" required>
                                <input className={inputCls} value={form.sampleTypeName}
                                    onChange={e => setForm(f => ({ ...f, sampleTypeName: e.target.value }))}
                                    placeholder="EDTA Blood" />
                            </Field>
                            <div className="grid grid-cols-2 gap-3">
                                <Field label="Department">
                                    <input className={inputCls} value={form.department}
                                        onChange={e => setForm(f => ({ ...f, department: e.target.value }))}
                                        placeholder="Hematology" />
                                </Field>
                                <Field label="Bottle Color">
                                    <select className={inputCls} value={form.bottleColor}
                                        onChange={e => setForm(f => ({ ...f, bottleColor: e.target.value }))}>
                                        <option value="">— Select —</option>
                                        {BOTTLE_COLOR_PRESETS.map(c => <option key={c}>{c}</option>)}
                                    </select>
                                </Field>
                            </div>
                            <Field label="Handling Instructions">
                                <textarea className={`${inputCls} resize-none`} rows={2} value={form.handlingInstructions}
                                    onChange={e => setForm(f => ({ ...f, handlingInstructions: e.target.value }))}
                                    placeholder="Collection and storage instructions…" />
                            </Field>
                            <div className="flex gap-2 justify-end pt-1">
                                <button type="button" onClick={() => setShowCreate(false)}
                                    className="px-4 py-2 rounded-xl text-[12px] font-bold cursor-pointer border border-[var(--v2-border-card)] text-[var(--v2-text-muted)] bg-[var(--v2-bg-surface)] hover:bg-[var(--v2-bg-card)]">
                                    Cancel
                                </button>
                                <button type="button" onClick={handleCreate} disabled={saving}
                                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-[12px] font-bold cursor-pointer text-white disabled:opacity-50 border-0"
                                    style={{ background: '#7C3AED' }}>
                                    <Save className="w-3.5 h-3.5" strokeWidth={2} /> {saving ? 'Adding…' : 'Add Type'}
                                </button>
                            </div>
                        </div>
                    </Modal>
                )}
            </SectionCard>
        );
    };

// ═══════════════════════════════════════════════════════════════════════════════
// SECTION 3 — PAYMENT MODES
// ═══════════════════════════════════════════════════════════════════════════════
const DEFAULT_PAYMENT_MODES = ['Cash', 'Card', 'UPI', 'Online', 'Insurance', 'Other'];

const PaymentModesSection: React.FC<{ items: PaymentModeData[]; loading: boolean; canManage: boolean; onSaved: () => void }> =
    ({ items, loading, canManage, onSaved }) => {
        const [showCreate, setShowCreate] = useState(false);
        const [saving, setSaving] = useState(false);
        const [form, setForm] = useState({ modeName: '' });

        const handleCreate = async () => {
            if (!form.modeName.trim()) { toast.error('Mode name is required'); return; }
            setSaving(true);
            try {
                await LabServices.updateLabConfig({ configType: 'PAYMENT_MODE', configId: form.modeName.trim().toUpperCase().replace(/\s+/g, '_'), modeName: form.modeName.trim() } as any);
                toast.success('Payment mode added');
                setShowCreate(false);
                setForm({ modeName: '' });
                onSaved();
            } catch (err: any) { toast.error(err?.message ?? 'Save failed'); }
            finally { setSaving(false); }
        };

        return (
            <SectionCard>
                <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--v2-border-subtle)]">
                    <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
                            style={{ background: 'linear-gradient(135deg,#0284C7,#0369A1)', boxShadow: '0 2px 8px rgba(2,132,199,.3)' }}>
                            <CreditCard className="w-4 h-4 text-white" strokeWidth={2} />
                        </div>
                        <div>
                            <p className="text-[13px] font-bold text-[var(--v2-text-heading)] m-0">Payment Modes</p>
                            <p className="text-[9px] text-[var(--v2-text-muted)] m-0">
                                {items.length > 0 ? `${items.length} configured` : 'Using defaults'}
                            </p>
                        </div>
                    </div>
                    {canManage && (
                        <button type="button" onClick={() => setShowCreate(true)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[11px] font-bold cursor-pointer text-white border-0"
                            style={{ background: '#0284C7' }}>
                            <Plus className="w-3.5 h-3.5" strokeWidth={2.5} /> Add Mode
                        </button>
                    )}
                </div>

                <div className="p-5">
                    {loading ? (
                        <div className="flex flex-wrap gap-2">
                            {[1, 2, 3, 4].map(i => <div key={i} className="h-9 w-20 rounded-xl" style={{ background: 'var(--v2-border-subtle)' }} />)}
                        </div>
                    ) : (
                        <>
                            {/* Show configured + highlight defaults if none configured */}
                            <div className="flex flex-wrap gap-2">
                                {(items.length > 0 ? items : DEFAULT_PAYMENT_MODES.map(m => ({ modeName: m, isActive: 'true' }))).map((mode, i) => {
                                    const name = (mode as PaymentModeData).modeName ?? (mode as any).mode_name ?? mode;
                                    const active = isActive((mode as PaymentModeData).isActive ?? 'true');
                                    return (
                                        <div key={i} className="flex items-center gap-1.5 px-3 py-2 rounded-xl border text-[12px] font-bold transition-colors"
                                            style={active
                                                ? { background: 'rgba(2,132,199,0.07)', border: '1px solid rgba(2,132,199,0.2)', color: '#0284C7' }
                                                : { background: 'var(--v2-bg-surface)', border: '1px solid var(--v2-border-card)', color: 'var(--v2-text-muted)' }}>
                                            <CreditCard className="w-3 h-3" strokeWidth={2} />
                                            {String(name)}
                                        </div>
                                    );
                                })}
                            </div>
                            {items.length === 0 && (
                                <p className="text-[10px] text-[var(--v2-text-muted)] mt-3 m-0">
                                    Showing default payment modes. Add custom ones to override.
                                </p>
                            )}
                        </>
                    )}
                </div>

                {showCreate && (
                    <Modal title="Add Payment Mode" onClose={() => setShowCreate(false)}>
                        <div className="flex flex-col gap-3">
                            <Field label="Mode Name" required>
                                <input className={inputCls} value={form.modeName}
                                    onChange={e => setForm(f => ({ ...f, modeName: e.target.value }))}
                                    placeholder="e.g. Cheque, NEFT" />
                            </Field>
                            <div className="flex flex-wrap gap-2">
                                {DEFAULT_PAYMENT_MODES.filter(m => !items.some(i => (i.modeName ?? i.mode_name) === m)).map(m => (
                                    <button key={m} type="button" onClick={() => setForm({ modeName: m })}
                                        className={`px-3 py-1.5 rounded-xl text-[11px] font-bold cursor-pointer border transition-colors ${form.modeName === m ? 'border-[#0284C7] bg-sky-50 text-sky-700' : 'border-[var(--v2-border-card)] bg-[var(--v2-bg-surface)] text-[var(--v2-text-muted)] hover:bg-[var(--v2-bg-card)]'}`}>
                                        {m}
                                    </button>
                                ))}
                            </div>
                            <div className="flex gap-2 justify-end pt-1">
                                <button type="button" onClick={() => setShowCreate(false)}
                                    className="px-4 py-2 rounded-xl text-[12px] font-bold cursor-pointer border border-[var(--v2-border-card)] text-[var(--v2-text-muted)] bg-[var(--v2-bg-surface)] hover:bg-[var(--v2-bg-card)]">
                                    Cancel
                                </button>
                                <button type="button" onClick={handleCreate} disabled={saving}
                                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-[12px] font-bold cursor-pointer text-white disabled:opacity-50 border-0"
                                    style={{ background: '#0284C7' }}>
                                    <Save className="w-3.5 h-3.5" strokeWidth={2} /> {saving ? 'Adding…' : 'Add Mode'}
                                </button>
                            </div>
                        </div>
                    </Modal>
                )}
            </SectionCard>
        );
    };

// ═══════════════════════════════════════════════════════════════════════════════
// SECTION 4 — TAX CONFIGURATION
// ═══════════════════════════════════════════════════════════════════════════════
const TaxConfigSection: React.FC<{ items: TaxConfigData[]; loading: boolean; canManage: boolean; onSaved: () => void }> =
    ({ items, loading, canManage, onSaved }) => {
        const [showCreate, setShowCreate] = useState(false);
        const [saving, setSaving] = useState(false);
        const [form, setForm] = useState({ taxName: '', rate: '' });

        const handleCreate = async () => {
            if (!form.taxName.trim() || !form.rate) { toast.error('Tax name and rate are required'); return; }
            setSaving(true);
            try {
                await LabServices.updateLabConfig({ configType: 'TAX_CONFIG', configId: form.taxName.trim().toUpperCase().replace(/\s+/g, '_'), taxName: form.taxName.trim(), rate: Number(form.rate) } as any);
                toast.success('Tax config added');
                setShowCreate(false);
                setForm({ taxName: '', rate: '' });
                onSaved();
            } catch (err: any) { toast.error(err?.message ?? 'Save failed'); }
            finally { setSaving(false); }
        };

        const taxPresets = [
            { taxName: 'CGST', rate: '6' }, { taxName: 'SGST', rate: '6' }, { taxName: 'IGST', rate: '12' }, { taxName: 'GST', rate: '18' },
        ];

        return (
            <SectionCard>
                <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--v2-border-subtle)]">
                    <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
                            style={{ background: 'linear-gradient(135deg,#D97706,#B45309)', boxShadow: '0 2px 8px rgba(217,119,6,.3)' }}>
                            <Percent className="w-4 h-4 text-white" strokeWidth={2.5} />
                        </div>
                        <div>
                            <p className="text-[13px] font-bold text-[var(--v2-text-heading)] m-0">Tax Configuration</p>
                            <p className="text-[9px] text-[var(--v2-text-muted)] m-0">
                                {items.length > 0 ? `${items.length} tax rules` : 'No tax rules configured'}
                            </p>
                        </div>
                    </div>
                    {canManage && (
                        <button type="button" onClick={() => setShowCreate(true)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[11px] font-bold cursor-pointer text-white border-0"
                            style={{ background: '#D97706' }}>
                            <Plus className="w-3.5 h-3.5" strokeWidth={2.5} /> Add Tax
                        </button>
                    )}
                </div>

                <div className="p-5">
                    {loading ? (
                        <div className="flex flex-col gap-2">
                            {[1, 2].map(i => <div key={i} className="h-14 rounded-xl" style={{ background: 'var(--v2-border-subtle)' }} />)}
                        </div>
                    ) : items.length === 0 ? (
                        <div className="flex flex-col items-center py-8 gap-2 text-center">
                            <Percent className="w-7 h-7 text-[var(--v2-text-muted)]" strokeWidth={1.5} />
                            <p className="text-[12px] font-semibold text-[var(--v2-text-muted)]">No tax rules set up</p>
                            {canManage && (
                                <div className="flex flex-wrap gap-2 justify-center mt-1">
                                    {taxPresets.map(tp => (
                                        <button key={tp.taxName} type="button"
                                            onClick={() => { setForm(tp); setShowCreate(true); }}
                                            className="px-3 py-1.5 rounded-xl text-[11px] font-bold cursor-pointer border border-amber-200 text-amber-700 bg-amber-50 hover:bg-amber-100 transition-colors">
                                            + {tp.taxName} {tp.rate}%
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                            {items.map((tax, i) => {
                                const name = tax.taxName ?? tax.tax_name ?? '—';
                                const active = isActive(tax.isActive ?? 'true');
                                return (
                                    <div key={tax.taxId ?? tax.SK ?? i}
                                        className="rounded-xl border border-[var(--v2-border-card)] bg-[var(--v2-bg-surface)] p-3 flex flex-col gap-1.5">
                                        <div className="flex items-center justify-between gap-1">
                                            <span className="text-[11px] font-bold text-[var(--v2-text-primary)]">{String(name)}</span>
                                            <ActiveBadge active={active} />
                                        </div>
                                        <span className="text-[22px] font-black tabular-nums" style={{ color: '#D97706' }}>
                                            {tax.rate ?? '—'}<span className="text-[14px]">%</span>
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {showCreate && (
                    <Modal title="Add Tax Rule" onClose={() => setShowCreate(false)}>
                        <div className="flex flex-col gap-3">
                            <Field label="Tax Name" required>
                                <input className={inputCls} value={form.taxName}
                                    onChange={e => setForm(f => ({ ...f, taxName: e.target.value }))}
                                    placeholder="CGST" />
                            </Field>
                            <Field label="Rate (%)" required>
                                <input type="number" className={inputCls} value={form.rate}
                                    onChange={e => setForm(f => ({ ...f, rate: e.target.value }))}
                                    placeholder="6" />
                            </Field>
                            <div className="flex flex-wrap gap-2">
                                {taxPresets.map(tp => (
                                    <button key={tp.taxName} type="button" onClick={() => setForm(tp)}
                                        className={`px-3 py-1.5 rounded-xl text-[11px] font-bold cursor-pointer border transition-colors ${form.taxName === tp.taxName ? 'border-amber-500 bg-amber-50 text-amber-700' : 'border-[var(--v2-border-card)] bg-[var(--v2-bg-surface)] text-[var(--v2-text-muted)] hover:bg-[var(--v2-bg-card)]'}`}>
                                        {tp.taxName} {tp.rate}%
                                    </button>
                                ))}
                            </div>
                            <div className="flex gap-2 justify-end pt-1">
                                <button type="button" onClick={() => setShowCreate(false)}
                                    className="px-4 py-2 rounded-xl text-[12px] font-bold cursor-pointer border border-[var(--v2-border-card)] text-[var(--v2-text-muted)] bg-[var(--v2-bg-surface)] hover:bg-[var(--v2-bg-card)]">
                                    Cancel
                                </button>
                                <button type="button" onClick={handleCreate} disabled={saving}
                                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-[12px] font-bold cursor-pointer text-white disabled:opacity-50 border-0"
                                    style={{ background: '#D97706' }}>
                                    <Save className="w-3.5 h-3.5" strokeWidth={2} /> {saving ? 'Adding…' : 'Add Tax'}
                                </button>
                            </div>
                        </div>
                    </Modal>
                )}
            </SectionCard>
        );
    };

// ═══════════════════════════════════════════════════════════════════════════════
// SECTION 5 — REFERENCE DOCTORS (sidebar list)
// ═══════════════════════════════════════════════════════════════════════════════
const ReferenceDoctorsSection: React.FC<{ canManage: boolean }> = ({ canManage }) => {
    const [doctors, setDoctors] = useState<ReferenceDoctor[]>([]);
    const [loading, setLoading] = useState(true);
    const [editDoc, setEditDoc] = useState<ReferenceDoctor | null>(null);
    const [showCreate, setShowCreate] = useState(false);
    const [saving, setSaving] = useState(false);
    const emptyForm = { doctorName: '', specialization: '', mobile: '', email: '', hospital: '', commissionPct: '0' };
    const [form, setForm] = useState(emptyForm);

    const load = async () => {
        setLoading(true);
        try { setDoctors(extractDoctorsList(await LabServices.listReferenceDoctors())); } catch { }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const openEdit = (d: ReferenceDoctor) => {
        setForm({
            doctorName: d.doctorName ?? d.doctor_name ?? '',
            specialization: d.specialization ?? '',
            mobile: d.mobile ?? '',
            email: d.email ?? '',
            hospital: d.hospital ?? '',
            commissionPct: String(d.commissionPct ?? d.commission_pct ?? 0),
        });
        setEditDoc(d);
    };

    const handleSave = async () => {
        if (!form.doctorName.trim()) { toast.error('Doctor name is required'); return; }
        setSaving(true);
        try {
            const payload = { ...form, commissionPct: Number(form.commissionPct) };
            if (editDoc) {
                const res: any = await LabServices.updateReferenceDoctor(extractDoctorId(editDoc), payload);
                toast.success(res?.message ?? 'Doctor updated'); setEditDoc(null);
            } else {
                // POST /lab/reference-doctors returns: { message: "Doctor created", doctorId }
                const res: any = await LabServices.createReferenceDoctor(payload);
                toast.success(res?.message ?? 'Doctor added'); setShowCreate(false);
            }
            load();
        } catch (err: any) { toast.error(err?.message ?? 'Save failed'); }
        finally { setSaving(false); }
    };

    const DoctorForm = (
        <div className="flex flex-col gap-3">
            <Field label="Doctor Name" required>
                <input className={inputCls} value={form.doctorName} onChange={e => setForm(f => ({ ...f, doctorName: e.target.value }))} placeholder="Dr. Sarah Jenkins" />
            </Field>
            <div className="grid grid-cols-2 gap-3">
                <Field label="Specialization">
                    <input className={inputCls} value={form.specialization} onChange={e => setForm(f => ({ ...f, specialization: e.target.value }))} placeholder="Cardiology" />
                </Field>
                <Field label="Mobile">
                    <input className={inputCls} value={form.mobile} onChange={e => setForm(f => ({ ...f, mobile: e.target.value }))} />
                </Field>
            </div>
            <Field label="Email">
                <input type="email" className={inputCls} value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
            </Field>
            <Field label="Hospital">
                <input className={inputCls} value={form.hospital} onChange={e => setForm(f => ({ ...f, hospital: e.target.value }))} />
            </Field>
            <Field label="Commission %">
                <input type="number" className={inputCls} value={form.commissionPct} onChange={e => setForm(f => ({ ...f, commissionPct: e.target.value }))} />
            </Field>
            <div className="flex gap-2 justify-end pt-1">
                <button type="button" onClick={() => { setShowCreate(false); setEditDoc(null); }}
                    className="px-4 py-2 rounded-xl text-[12px] font-bold cursor-pointer border border-[var(--v2-border-card)] text-[var(--v2-text-muted)] bg-[var(--v2-bg-surface)] hover:bg-[var(--v2-bg-card)]">
                    Cancel
                </button>
                <button type="button" onClick={handleSave} disabled={saving}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-[12px] font-bold cursor-pointer text-white disabled:opacity-50 border-0"
                    style={{ background: '#059669' }}>
                    <Save className="w-3.5 h-3.5" strokeWidth={2} /> {saving ? 'Saving…' : (editDoc ? 'Update' : 'Add Doctor')}
                </button>
            </div>
        </div>
    );

    return (
        <SectionCard>
            <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--v2-border-subtle)]">
                <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ background: 'linear-gradient(135deg,#DC2626,#B91C1C)', boxShadow: '0 2px 8px rgba(220,38,38,.3)' }}>
                        <span className="text-white font-black text-[11px]">Rx</span>
                    </div>
                    <div>
                        <p className="text-[13px] font-bold text-[var(--v2-text-heading)] m-0">Reference Doctors</p>
                        <p className="text-[9px] text-[var(--v2-text-muted)] m-0">{doctors.length} registered</p>
                    </div>
                </div>
                {canManage && (
                    <button type="button" onClick={() => { setForm(emptyForm); setShowCreate(true); }}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[11px] font-bold cursor-pointer text-white border-0"
                        style={{ background: '#DC2626' }}>
                        <Plus className="w-3.5 h-3.5" strokeWidth={2.5} /> Add Doctor
                    </button>
                )}
            </div>

            {loading ? (
                <div className="p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {[1, 2].map(i => <div key={i} className="h-24 rounded-xl" style={{ background: 'var(--v2-border-subtle)' }} />)}
                </div>
            ) : doctors.length === 0 ? (
                <div className="flex flex-col items-center py-12 gap-2 text-center">
                    <p className="text-[12px] font-semibold text-[var(--v2-text-muted)]">No reference doctors yet</p>
                    {canManage && (
                        <button type="button" onClick={() => { setForm(emptyForm); setShowCreate(true); }}
                            className="px-3 py-1.5 rounded-xl text-[11px] font-bold cursor-pointer text-white border-0 mt-1" style={{ background: '#DC2626' }}>
                            Add first doctor
                        </button>
                    )}
                </div>
            ) : (
                <div className="p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {doctors.map(d => (
                        <div key={extractDoctorId(d)}
                            className="rounded-xl border border-[var(--v2-border-card)] bg-[var(--v2-bg-surface)] p-3.5 flex flex-col gap-2 hover:border-red-200 transition-colors">
                            <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0">
                                    <p className="text-[12px] font-bold text-[var(--v2-text-heading)] m-0 truncate">{extractDoctorName(d)}</p>
                                    {d.specialization && <p className="text-[10px] text-[var(--v2-text-muted)] m-0">{d.specialization}</p>}
                                </div>
                                <span className="text-[10px] font-black px-2 py-0.5 rounded-full flex-shrink-0"
                                    style={{ background: 'rgba(220,38,38,0.08)', color: '#DC2626' }}>
                                    {d.commissionPct ?? d.commission_pct ?? 0}%
                                </span>
                            </div>
                            {d.hospital && <p className="text-[10px] text-[var(--v2-text-muted)] m-0">{d.hospital}</p>}
                            {d.mobile && <p className="text-[10px] text-[var(--v2-text-muted)] m-0">{d.mobile}</p>}
                            {canManage && (
                                <button type="button" onClick={() => openEdit(d)}
                                    className="self-start inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-semibold cursor-pointer border border-[var(--v2-border-card)] text-[var(--v2-text-muted)] hover:text-[var(--v2-text-primary)] bg-transparent transition-all mt-0.5">
                                    <Edit2 className="w-3 h-3" strokeWidth={2} /> Edit
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {(showCreate || editDoc) && (
                <Modal title={editDoc ? 'Edit Doctor' : 'Add Reference Doctor'} onClose={() => { setShowCreate(false); setEditDoc(null); }}>
                    {DoctorForm}
                </Modal>
            )}
        </SectionCard>
    );
};

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN EXPORT — single-load, all sections on one page
// ═══════════════════════════════════════════════════════════════════════════════
export const LabSettings: React.FC = () => {
    const { canManageCatalog } = useLabPermissions();
    const [config, setConfig] = useState<FullConfig | null>(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    const load = useCallback(async (silent = false) => {
        if (!silent) setLoading(true); else setRefreshing(true);
        try {
            const res = await LabServices.getLabConfig();
            const c = res?.config ?? res?.data ?? res;

            // API sometimes places TAX_CONFIG items inside lab_profile array — split them
            const rawProfile: any[] = Array.isArray(c?.lab_profile) ? c.lab_profile : [];
            const realProfiles = rawProfile.filter((p: any) => p?.configType !== 'TAX_CONFIG' && p?.SK !== 'TAX_CONFIG');
            const misplacedTax = rawProfile.filter((p: any) => p?.configType === 'TAX_CONFIG' || p?.SK === 'TAX_CONFIG');
            const rawTax: any[] = Array.isArray(c?.tax_config) ? c.tax_config : [];

            setConfig({
                lab_profile: realProfiles,
                sample_type: Array.isArray(c?.sample_type) ? c.sample_type : [],
                payment_mode: Array.isArray(c?.payment_mode) ? c.payment_mode : [],
                tax_config: [...rawTax, ...misplacedTax],
            });
        } catch { toast.error('Failed to load settings'); }
        finally { setLoading(false); setRefreshing(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    // Section nav on the left sidebar (desktop)
    const SECTIONS = [
        { id: 'profile', label: 'Lab Profile', color: '#059669' },
        { id: 'samples', label: 'Sample Types', color: '#7C3AED' },
        { id: 'payment', label: 'Payment Modes', color: '#0284C7' },
        { id: 'tax', label: 'Tax Config', color: '#D97706' },
        { id: 'doctors', label: 'Ref. Doctors', color: '#DC2626' },
    ] as const;

    return (
        <div className="flex flex-col gap-1 v2-animate-fade-in">
            {/* ── Page header ─────────────────────────────── */}
            <div className="flex items-center justify-between mb-4">
                <div>
                    <h1 className="text-[18px] font-black text-[var(--v2-text-heading)] m-0 leading-tight">Settings</h1>
                    <p className="text-[11px] text-[var(--v2-text-muted)] m-0 mt-0.5">
                        Configure your lab's profile, billing, and diagnostic parameters
                    </p>
                </div>
                <button type="button" onClick={() => load(true)} disabled={refreshing}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[11px] font-bold cursor-pointer border border-[var(--v2-border-card)] text-[var(--v2-text-muted)] hover:text-[var(--v2-text-primary)] bg-[var(--v2-bg-surface)] hover:bg-[var(--v2-bg-card)] disabled:opacity-50 transition-all">
                    <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} strokeWidth={2} />
                    Refresh
                </button>
            </div>

            {/* ── Quick-jump pills (mobile-friendly) ─────── */}
            <div className="flex items-center gap-1.5 flex-wrap mb-4">
                {SECTIONS.map(s => (
                    <a key={s.id} href={`#settings-${s.id}`}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold no-underline transition-all"
                        style={{ background: `${s.color}14`, color: s.color, border: `1px solid ${s.color}30` }}>
                        <ChevronRight className="w-3 h-3" strokeWidth={2.5} />
                        {s.label}
                    </a>
                ))}
            </div>

            {/* ── All settings sections stacked ──────────── */}
            <div className="flex flex-col gap-5">
                <div id="settings-profile">
                    <LabProfileSection
                        profile={config?.lab_profile?.[0] ?? null}
                        loading={loading}
                        canManage={canManageCatalog}
                        onSaved={() => load(true)}
                    />
                </div>

                <div id="settings-samples">
                    <SampleTypesSection
                        items={config?.sample_type ?? []}
                        loading={loading}
                        canManage={canManageCatalog}
                        onSaved={() => load(true)}
                    />
                </div>

                <div id="settings-payment">
                    <PaymentModesSection
                        items={config?.payment_mode ?? []}
                        loading={loading}
                        canManage={canManageCatalog}
                        onSaved={() => load(true)}
                    />
                </div>

                <div id="settings-tax">
                    <TaxConfigSection
                        items={config?.tax_config ?? []}
                        loading={loading}
                        canManage={canManageCatalog}
                        onSaved={() => load(true)}
                    />
                </div>

                <div id="settings-doctors">
                    <ReferenceDoctorsSection canManage={canManageCatalog} />
                </div>
            </div>
        </div>
    );
};

export default LabSettings;
